import {
  Button,
  Collapse,
  ColumnTable,
  Table,
  TextOutlinedInput,
  addToast,
} from '@octano/global-ui';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form } from 'reactstrap';
import { saveCohortCell } from '../../../../api/requests/tuitionContinuityTariff';
import {
  TariffPaymentMethodSettingValue,
  TuitionPaymentType,
} from '../../../../types/tariffTypes';
import {
  CohortRange,
  TariffListResponse,
  TcTariffSubmitData,
  TcTariffSubmitPendingData,
  TcTariffTableData,
} from '../../../../types/tuitionContinuityTrariff';
import {
  checkCurrencyAsNumber,
  TariffFormatter,
} from '../../../../utils/currency';
import CustomBadge from '../../../AdmissionAndEnrollments/TariffAndTuition/Tariff/parts/TariffCustomBadge';

interface TcTariffAccordionProps {
  row: TcTariffTableData;
  onFormatData: (data: {
    data: TcTariffSubmitData[];
    pending: TcTariffSubmitPendingData[];
  }) => void;
  periodId: number;
  isLoading: boolean;
  texts: {
    accordion: Record<string, string>;
  };
  fetchedConfigs?: TariffPaymentMethodSettingValue | null;
  cohortEnabled?: boolean;
  cohortRanges?: CohortRange[];
  onCohortRefetch?: () => void;
  onEditRange?: (range: CohortRange) => void;
  onDeleteRange?: (range: CohortRange) => void;
}

interface IForm {
  [key: string]: string;
}

interface ISubmitForm {
  offers: {
    tariff: string;
    credit?: string;
    tuition?: string;
  }[];
}

// Fila de la tabla inline de cohorte: cruce (oferta × rango compartido).
interface CohortRowData {
  offer: TariffListResponse;
  range: CohortRange;
}

export default function TcTariffAccordion({
  row,
  onFormatData,
  periodId,
  isLoading,
  texts,
  fetchedConfigs,
  cohortEnabled,
  cohortRanges,
  onCohortRefetch,
  onEditRange,
  onDeleteRange,
}: TcTariffAccordionProps) {
  const { t } = useTranslation();
  const { handleSubmit, control } = useForm<IForm>({
    mode: 'onChange',
  });
  const {
    handleSubmit: handleCohortSubmit,
    control: cohortControl,
  } = useForm<IForm>({
    mode: 'onChange',
  });

  const finalRow = useCallback((data) => data!.length - 1, []);
  const [globalStatus, setGlobalStatus] = useState<number | undefined>(1);
  const [cohortSubmitting, setCohortSubmitting] = useState<boolean>(false);

  const showCohortTable = Boolean(
    cohortEnabled && cohortRanges && cohortRanges.length > 0,
  );

  const submitData = useCallback(
    (data: ISubmitForm) => {
      /**
       * El indice de los formularios contienen el
       * academicOfferStudyPlanVersionOfferId (offerId).
       */
      const updatedOffer = data.offers.reduce(
        (acc, inputs, offerId) => {
          const offer = row.offers.find((x) => x.offerId === offerId);
          const finalValue = inputs.tariff
            ? checkCurrencyAsNumber(inputs.tariff)
            : 0;
          const creditValue = inputs?.credit
            ? checkCurrencyAsNumber(inputs.credit)
            : 0;
          const tuitionValue = inputs?.tuition
            ? checkCurrencyAsNumber(inputs.tuition)
            : 0;

          if (finalValue > 0 || creditValue > 0 || tuitionValue > 0) {
            acc.data.push({
              offerId: offerId,
              periodId,
              tariff: {
                id: offer?.tariff?.id,
                value: finalValue,
                credit: creditValue,
                tuition: tuitionValue,
              },
              offerPeriodId: offer?.offerPeriodId,
            });
          } else if (offer) {
            acc.pending.push({
              campusName: offer.campusName,
              scheduleName: offer.scheduleName,
              studyPlanName: offer.studyPlanName,
            });
          }

          return acc;
        },
        {
          data: new Array<TcTariffSubmitData>(),
          pending: new Array<TcTariffSubmitPendingData>(),
        },
      );
      onFormatData(updatedOffer);
    },
    [onFormatData, periodId, row.offers],
  );

  useEffect(() => {
    // En modo cohorte el tariff base no aplica (queda en 0): el estado del
    // plan depende de que TODAS las celdas (oferta × rango) tengan precio,
    // igual que el badge por fila.
    if (showCohortTable) {
      const allCellsSaved = row.offers.every((offer) =>
        (cohortRanges ?? []).every((range) =>
          offer.cohortCells?.some((cell) => cell.cohortRangeId === range.id),
        ),
      );
      setGlobalStatus(allCellsSaved ? 1 : undefined);
      return;
    }
    let status: number | undefined = 1;
    for (const offer of row.offers) {
      if (
        fetchedConfigs?.tuitionPaymentType ===
        TuitionPaymentType.TuitionContractCredits
      ) {
        // Presencia, no monto: un crédito guardado en 0 cuenta como
        // configurado (mismo criterio que isOfferComplete del tab Inicio).
        if (!offer.tariff || offer.tariff.credit == null) {
          status = undefined;
          break;
        }
      }

      if (!offer.tariff) {
        status = undefined;
        break;
      }
    }
    setGlobalStatus(status);
  }, [
    row.offers,
    fetchedConfigs?.tuitionPaymentType,
    showCohortTable,
    cohortRanges,
  ]);

  const printClassName = (
    index: number,
    data: Partial<TariffListResponse>[],
    className?: string,
  ) => {
    return clsx(className, index === data.length - 1 && 'bg-white');
  };

  const columns = useMemo<ColumnTable<Partial<TariffListResponse>>[]>(
    () => [
      {
        columnName: 'campusName',
        bold: false,
        headerText: texts.accordion.campus,
        width: '20%',
        tdClassName: ({ index, data }) => printClassName(index, data!),
        cellFormat: (options) => {
          return <p>{options.row.campusName}</p>;
        },
      },
      {
        columnName: 'schedule',
        headerText: texts.accordion.schedule,
        width: '20%',
        tdClassName: ({ index, data }) => printClassName(index, data!),
        cellFormat: (options) => {
          return <p>{options.row.scheduleName}</p>;
        },
      },
      {
        columnName: 'tuition',
        headerText: texts.accordion.tuition,
        width: '20%',
        tdClassName: ({ index, data }) => printClassName(index, data!),
        cellFormat: ({ row: offer, index, data }) => {
          if (finalRow(data) === index) {
            return null;
          }
          const inputName = `offers[${offer.offerId}].tuition`;
          return (
            <TextOutlinedInput
              textAlign="center"
              type="text"
              name={inputName}
              defaultValue={TariffFormatter(
                offer.tariff?.tuition ?? undefined,
                offer.tariff?.currency,
              )}
              formatter={(value) =>
                TariffFormatter(value, offer.tariff?.currency)
              }
              control={control}
            />
          );
        },
      },
      {
        columnName: 'tariff',
        headerText: texts.accordion.tariff,
        width: '20%',
        tdClassName: ({ index, data }) => printClassName(index, data!),
        cellFormat: ({ row: offer, index, data }) => {
          if (finalRow(data) === index) {
            return null;
          }
          const inputName = `offers[${offer.offerId}].tariff`;
          return (
            <TextOutlinedInput
              textAlign="center"
              type="text"
              name={inputName}
              defaultValue={TariffFormatter(
                offer.tariff?.value,
                offer.tariff?.currency,
              )}
              formatter={(value) =>
                TariffFormatter(value, offer.tariff?.currency)
              }
              control={control}
            />
          );
        },
      },
    ],
    [texts, finalRow, control],
  );

  const renderColumns = useMemo((): ColumnTable<
    Partial<TariffListResponse>
  >[] => {
    if (
      fetchedConfigs?.tuitionPaymentType ===
      TuitionPaymentType.TuitionContractCredits
    ) {
      // Con la columna Crédito son 6 columnas: se redistribuyen los anchos
      // para que sumen 100% (sin esto, Crédito queda sin width y descuadra
      // Matrícula/Arancel/Crédito).
      return [
        ...columns.map((column) => ({ ...column, width: '16%' })),
        {
          columnName: 'newCredit',
          headerText: texts.accordion.credit,
          width: '16%',
          tdClassName: ({ index, data }) => printClassName(index, data!),
          cellFormat: ({ row: offer, index, data }) => {
            if (finalRow(data) === index) {
              return null;
            }
            const inputName = `offers[${offer?.offerId}].credit`;
            return (
              <TextOutlinedInput
                textAlign="center"
                type="text"
                name={inputName}
                defaultValue={TariffFormatter(
                  offer.tariff?.credit,
                  offer.tariff?.currency,
                )}
                formatter={(value) =>
                  TariffFormatter(value, offer.tariff?.currency)
                }
                control={control}
              />
            );
          },
        },
        {
          columnName: 'status',
          headerText: texts.accordion.status,
          width: '20%',
          tdClassName: ({ index, data }) =>
            printClassName(index, data!, 'text-center'),
          cellFormat: ({ row: offer, index, data }) => {
            if (finalRow(data) === index) {
              return (
                <Button
                  type="submit"
                  text={texts.accordion.submitButtonAll}
                  size="sm"
                  loading={isLoading}
                  fullwidth
                />
              );
            }
            // Presencia, no monto: crédito 0 guardado = configurado (mismo
            // criterio que isOfferComplete del tab Inicio).
            return (
              <CustomBadge
                value={
                  offer.tariff?.value == null && offer.tariff?.credit == null
                    ? undefined
                    : 1
                }
              />
            );
          },
        },
      ];
    }

    return [
      ...columns,
      {
        columnName: 'status',
        headerText: texts.accordion.status,
        width: '20%',
        tdClassName: ({ index, data }) =>
          printClassName(index, data!, 'text-center'),
        cellFormat: ({ row: offer, index, data }) => {
          if (finalRow(data) === index) {
            return (
              <Button
                type="submit"
                text={texts.accordion.submitButtonAll}
                size="sm"
                loading={isLoading}
                fullwidth
              />
            );
          }
          // `offer.tariff.value` llega como string (Postgres numeric →
          // TypeORM string), y CustomBadge exige `typeof === 'number'`. Sin
          // coerción, un arancel guardado ("10.00") se ve como "Pendiente".
          return (
            <CustomBadge
              value={
                offer.tariff?.value != null
                  ? Number(offer.tariff.value)
                  : undefined
              }
            />
          );
        },
      },
    ];
  }, [
    control,
    columns,
    texts,
    fetchedConfigs?.tuitionPaymentType,
    finalRow,
    isLoading,
  ]);

  // Filas de la tabla inline de cohorte = (ofertas del plan × rangos compartidos).
  const cohortRows = useMemo<CohortRowData[]>(() => {
    if (!showCohortTable || !cohortRanges) {
      return [];
    }
    const rows: CohortRowData[] = [];
    for (const offer of row.offers) {
      for (const range of cohortRanges) {
        rows.push({ offer, range });
      }
    }
    return rows;
  }, [showCohortTable, cohortRanges, row.offers]);

  const cohortColumns = useMemo<ColumnTable<CohortRowData>[]>(() => {
    const showCohortCredit =
      fetchedConfigs?.tuitionPaymentType ===
      TuitionPaymentType.TuitionContractCredits;
    const findCell = (cohortRow: CohortRowData) =>
      cohortRow.offer.cohortCells?.find(
        (item) => item.cohortRangeId === cohortRow.range.id,
      );
    const cols: ColumnTable<CohortRowData>[] = [
      {
        columnName: 'cohort',
        headerText: t('tuitionContinuityTariff.cohort.cohortColumn'),
        width: '15%',
        cellFormat: ({ row: cohortRow, index, data }) =>
          finalRow(data) === index ? null : (
            <p>{`${cohortRow.range.fromYear} - ${cohortRow.range.toYear}`}</p>
          ),
      },
      {
        columnName: 'campusName',
        headerText: texts.accordion.campus,
        width: '15%',
        cellFormat: ({ row: cohortRow, index, data }) =>
          finalRow(data) === index ? null : <p>{cohortRow.offer.campusName}</p>,
      },
      {
        columnName: 'schedule',
        headerText: texts.accordion.schedule,
        width: '15%',
        cellFormat: ({ row: cohortRow, index, data }) =>
          finalRow(data) === index ? null : (
            <p>{cohortRow.offer.scheduleName}</p>
          ),
      },
      {
        columnName: 'tuition',
        headerText: texts.accordion.tuition,
        width: '15%',
        cellFormat: ({ row: cohortRow, index, data }) => {
          if (finalRow(data) === index) {
            return null;
          }
          const cell = findCell(cohortRow);
          const inputName = `cohort_${cohortRow.offer.offerId}_${cohortRow.range.id}_tuition`;
          return (
            <TextOutlinedInput
              textAlign="center"
              type="text"
              name={inputName}
              defaultValue={TariffFormatter(
                cell?.tuition ?? undefined,
                cohortRow.offer.tariff?.currency,
              )}
              formatter={(value) =>
                TariffFormatter(value, cohortRow.offer.tariff?.currency)
              }
              control={cohortControl}
            />
          );
        },
      },
      {
        columnName: 'tariff',
        headerText: texts.accordion.tariff,
        width: '15%',
        cellFormat: ({ row: cohortRow, index, data }) => {
          if (finalRow(data) === index) {
            return null;
          }
          const cell = findCell(cohortRow);
          const inputName = `cohort_${cohortRow.offer.offerId}_${cohortRow.range.id}_value`;
          return (
            <TextOutlinedInput
              textAlign="center"
              type="text"
              name={inputName}
              defaultValue={TariffFormatter(
                cell?.value ?? undefined,
                cohortRow.offer.tariff?.currency,
              )}
              formatter={(value) =>
                TariffFormatter(value, cohortRow.offer.tariff?.currency)
              }
              control={cohortControl}
            />
          );
        },
      },
    ];

    if (showCohortCredit) {
      cols.push({
        columnName: 'credit',
        headerText: texts.accordion.credit,
        width: '12%',
        cellFormat: ({ row: cohortRow, index, data }) => {
          if (finalRow(data) === index) {
            return null;
          }
          const cell = findCell(cohortRow);
          const inputName = `cohort_${cohortRow.offer.offerId}_${cohortRow.range.id}_credit`;
          return (
            <TextOutlinedInput
              textAlign="center"
              type="text"
              name={inputName}
              defaultValue={TariffFormatter(
                cell?.credit ?? undefined,
                cohortRow.offer.tariff?.currency,
              )}
              formatter={(value) =>
                TariffFormatter(value, cohortRow.offer.tariff?.currency)
              }
              control={cohortControl}
            />
          );
        },
      });
    }

    cols.push(
      {
        columnName: 'status',
        headerText: texts.accordion.status,
        width: '15%',
        tdClassName: 'text-center',
        cellFormat: ({ row: cohortRow, index, data }) => {
          if (finalRow(data) === index) {
            return (
              <Button
                type="submit"
                text={texts.accordion.submitButtonAll}
                size="sm"
                loading={cohortSubmitting}
                fullwidth
              />
            );
          }
          const cell = findCell(cohortRow);
          return <CustomBadge value={cell?.id != null ? 1 : undefined} />;
        },
      },
      {
        columnName: 'action',
        headerText: t('tuitionContinuityTariff.cohort.action'),
        width: '10%',
        tdClassName: 'text-center',
        cellFormat: ({ row: cohortRow, index, data }) => {
          if (finalRow(data) === index) {
            return null;
          }
          return (
            <div
              className="d-flex justify-content-center"
              style={{ gap: '0.5rem' }}
            >
              <Button
                type="button"
                text=""
                size="sm"
                outlined
                icon="edit"
                onClick={() => {
                  if (onEditRange) {
                    onEditRange(cohortRow.range);
                  }
                }}
              />
              <Button
                type="button"
                text=""
                size="sm"
                outlined
                icon="trash"
                // El cohorte es irreversible: siempre debe quedar >=1 rango, así
                // que no se permite borrar el último (evita el estado atascado
                // "cohorte activo sin rangos").
                disabled={(cohortRanges?.length ?? 0) <= 1}
                onClick={() => {
                  if (onDeleteRange) {
                    onDeleteRange(cohortRow.range);
                  }
                }}
              />
            </div>
          );
        },
      },
    );

    return cols;
  }, [
    t,
    texts,
    cohortControl,
    onEditRange,
    onDeleteRange,
    fetchedConfigs?.tuitionPaymentType,
    finalRow,
    cohortSubmitting,
    cohortRanges,
  ]);

  // CONFIRMAR de la tabla de cohorte: guarda cada celda editada (no vacía).
  const submitCohort = useCallback(
    async (data: IForm) => {
      setCohortSubmitting(true);
      let saved = false;
      for (const offer of row.offers) {
        if (!cohortRanges) {
          continue;
        }
        for (const range of cohortRanges) {
          const valueRaw = data[`cohort_${offer.offerId}_${range.id}_value`];
          const tuitionRaw =
            data[`cohort_${offer.offerId}_${range.id}_tuition`];
          const creditRaw = data[`cohort_${offer.offerId}_${range.id}_credit`];
          const value = valueRaw ? checkCurrencyAsNumber(valueRaw) : 0;
          const tuition = tuitionRaw ? checkCurrencyAsNumber(tuitionRaw) : 0;
          const credit = creditRaw ? checkCurrencyAsNumber(creditRaw) : 0;
          if (value <= 0 && tuition <= 0 && credit <= 0) {
            continue;
          }
          // Si la oferta aún no tiene offer-period, el backend lo crea a
          // partir de offerId + periodId.
          const { error } = await saveCohortCell({
            curriculumOfferPeriodId: offer.offerPeriodId ?? undefined,
            offerId: offer.offerId,
            periodId: offer.periodId,
            cohortRangeId: range.id,
            value,
            tuition,
            credit,
          });
          if (error) {
            addToast({
              icon: 'error',
              color: 'danger',
              text: t('common.errors.save'),
            });
            setCohortSubmitting(false);
            return;
          }
          saved = true;
        }
      }
      if (saved) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('tuitionContinuityTariff.cohort.cellSaveSuccess'),
        });
      }
      if (onCohortRefetch) {
        onCohortRefetch();
      }
      setCohortSubmitting(false);
    },
    [row.offers, cohortRanges, onCohortRefetch, t],
  );

  if (showCohortTable) {
    return (
      <Form
        key={`cohort-${row.id}`}
        onSubmit={handleCohortSubmit(submitCohort)}
      >
        <Collapse
          renderTitle={() => (
            <p className="fs-14 text-dark fw-400">{row.name}</p>
          )}
          renderBtn={({ isOpened, toggle }) => (
            <>
              <CustomBadge value={globalStatus} className="ml-auto" />
              <Collapse.ToggleButton
                iconName={isOpened ? 'minus' : 'plus'}
                toggle={toggle}
                btnClassName="bg-primary ml-3"
                iconColor="white"
              />
            </>
          )}
        >
          <Table
            key={(cohortRanges ?? []).map((range) => range.id).join('-')}
            columns={cohortColumns}
            data={[...cohortRows, {} as CohortRowData]}
            striped
            color="transparent"
            borderless={false}
          />
        </Collapse>
      </Form>
    );
  }

  return (
    <Form key={row.id} onSubmit={handleSubmit(submitData)}>
      <Collapse
        renderTitle={() => <p className="fs-14 text-dark fw-400">{row.name}</p>}
        renderBtn={({ isOpened, toggle }) => (
          <>
            <CustomBadge value={globalStatus} className="ml-auto" />
            <Collapse.ToggleButton
              iconName={isOpened ? 'minus' : 'plus'}
              toggle={toggle}
              btnClassName="bg-primary ml-3"
              iconColor="white"
            />
          </>
        )}
      >
        <Table
          columns={renderColumns}
          data={[...row.offers, {}]}
          striped
          color="transparent"
          borderless={false}
        />
      </Collapse>
    </Form>
  );
}
